import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import Navbar from "./Navbar";
import AuthService from "../auth/authService";
import API_CONFIG, { buildApiUrl, buildApiUrlWithParams, buildS3Url } from '../config/api';

const Attendance = () => {
    const [userGroups, setUserGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [classes, setClasses] = useState([]);
    const [selectedClass, setSelectedClass] = useState(null);
    const [attendanceRecords, setAttendanceRecords] = useState([]);
    const [studentRoles, setStudentRoles] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [showImageModal, setShowImageModal] = useState(false);
    const [selectedImageUrl, setSelectedImageUrl] = useState("");
    const [selectedStudentName, setSelectedStudentName] = useState("");

    const fetchAttendanceRecords = async () => {
        if (!selectedClass) return;

        try {
            const token = AuthService.getToken();
            const response = await fetch(
                buildApiUrlWithParams(API_CONFIG.ENDPOINTS.ATTENDANCE, selectedClass?.class_id, selectedDate),
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (!response.ok) {
                throw new Error("Failed to fetch attendance records");
            }

            const data = await response.json();
            // Set present to true for all records
            const recordsWithPresent = data.map(record => ({
                ...record
            }));
            setAttendanceRecords(recordsWithPresent);
        } catch (error) {
            console.error("Error fetching attendance records:", error);
        }
    };

    useEffect(() => {
        const token = AuthService.getToken();
        if (token) {
            const decoded = JSON.parse(atob(token.split(".")[1]));
            setUserGroups(decoded["cognito:groups"]);
        }
    }, []);

    useEffect(() => {
        const fetchStudentRoles = async () => {
            try {
                const token = AuthService.getToken();
                const response = await fetch(
                    buildApiUrl(API_CONFIG.ENDPOINTS.STUDENT_ROLES),
                    {
                        method: "GET",
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Content-Type": "application/json",
                        },
                    }
                );

                if (!response.ok) {
                    throw new Error("Failed to fetch student roles");
                }

                const data = await response.json();
                setStudentRoles(data);
            } catch (error) {
                console.error("Error fetching student roles:", error);
            }
        };

        fetchStudentRoles();
    }, []);

    useEffect(() => {
        const fetchClasses = async () => {
            try {
                const token = AuthService.getToken();
                // Convert selectedDate to Eastern Time
                const date = new Date(selectedDate + 'T00:00:00-05:00'); // Set to EST
                const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, etc.

                const response = await fetch(
                    buildApiUrlWithParams(API_CONFIG.ENDPOINTS.CLASSES_BY_DAY, dayOfWeek),
                    {
                        method: "GET",
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Content-Type": "application/json",
                        },
                    }
                );

                if (!response.ok) {
                    throw new Error("Failed to fetch classes");
                }

                const data = await response.json();
                setClasses(data);
                setLoading(false);
            } catch (error) {
                console.error("Error fetching classes:", error);
                setLoading(false);
            }
        };

        fetchClasses();
    }, [selectedDate]);

    useEffect(() => {
        fetchAttendanceRecords();
    }, [selectedClass, selectedDate]);

    const handleDateChange = (e) => {
        setSelectedDate(e.target.value);
    };

    const handleClassChange = (e) => {
        const classId = parseInt(e.target.value);
        const selected = classes.find(c => c.class_id === classId);
        setSelectedClass(selected);
    };

    const handleAttendanceChange = (studentId, present) => {
        setAttendanceRecords(prevRecords => 
            prevRecords.map(record => 
                record.student_id === studentId 
                    ? { ...record, present } 
                    : record
            )
        );
    };

    const handleRoleChange = (studentId, studentRoleId) => {
        setAttendanceRecords(prevRecords => 
            prevRecords.map(record => 
                record.student_id === studentId 
                    ? { ...record, student_role_id: studentRoleId } 
                    : record
            )
        );
    };

    const handleSave = async () => {
        try {
            // Filter records to only include group 1 or present=true
            const recordsToSave = attendanceRecords.filter(record => 
                record.grouping === "1" || record.present
            );

            const response = await fetch(buildApiUrlWithParams(API_CONFIG.ENDPOINTS.ATTENDANCE_SAVE, selectedClass?.class_id, selectedDate), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${AuthService.getToken()}`
                },
                body: JSON.stringify(recordsToSave)
            });

            const data = await response.json();
            
            if (response.ok) {
                alert('Attendance records saved successfully!');
                // Reload attendance records
                fetchAttendanceRecords();
            } else {
                alert(`Error saving attendance: ${data.error || 'Unknown error'}`);
            }
        } catch (error) {
            alert(`Error saving attendance: ${error.message}`);
        }
    };

    const getRoleDescription = (roleId) => {
        const role = studentRoles.find(r => r.student_role_id === roleId);
        return role ? role.student_role_desc : "No Role";
    };

    const handleImageClick = (imageUrl, studentName) => {
        setSelectedImageUrl(imageUrl);
        setSelectedStudentName(studentName);
        setShowImageModal(true);
    };

    const filteredRecords = attendanceRecords.filter(record => {
        // Always show students in groups 1-3
        if (record.grouping < "4") return true;

        if (searchQuery === "" && !record.present) return false;
        // For group 4, only show if present is true or matches search
        if (record.grouping === "4") {
            const matchesSearch =  
                record.first_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                record.last_name.toLowerCase().includes(searchQuery.toLowerCase());
            return record.present || matchesSearch;
        }
        
        return false;
    });

    if (loading) {
        return (
            <div className="container">
                <Navbar groups={userGroups}/>
                <div className="card">
                    <div className="card-body">
                        <div className="spinner-border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container">
            <Navbar groups={userGroups}/>
            <div className="card p-4 shadow-lg">
                <h2 className="text-center text-primary mb-4">Attendance</h2>
                
                <div className="row mb-4">
                    <div className="col-md-6">
                        <label className="form-label">Date:</label>
                        <input
                            type="date"
                            className="form-control"
                            value={selectedDate}
                            onChange={handleDateChange}
                        />
                    </div>
                    <div className="col-md-6">
                        <label className="form-label">Class:</label>
                        <select
                            className="form-control"
                            value={selectedClass?.class_id || ""}
                            onChange={handleClassChange}
                        >
                            <option value="">Select a class</option>
                            {classes.map((classItem) => (
                                <option key={classItem.class_id} value={classItem.class_id}>
                                    {classItem.class_name} - {classItem.start_time}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {selectedClass && (
                    <>
                        <div className="row mb-4">
                            <div className="col-12">
                                <button 
                                    className="btn btn-primary w-100"
                                    onClick={handleSave}
                                    disabled={!selectedClass || !selectedDate}
                                >
                                    Save Attendance
                                </button>
                            </div>
                        </div>
                        <div className="table-responsive">
                            <table className="table table-dark table-hover table-bordered">
                                <thead className="thead-light">
                                    <tr>
                                        <th style={{ width: '80px' }}>Picture</th>
                                        <th>Name</th>
                                        <th>Rank</th>
                                        <th>Role</th>
                                        <th>Present</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredRecords.map((record, index) => {
                                        const showSeparator = index > 0 && 
                                            record.grouping !== filteredRecords[index - 1].grouping;
                                        
                                        return (
                                            <React.Fragment key={record.student_id}>
                                                {showSeparator && (
                                                    <tr className="table-secondary">
                                                        <td colSpan="5" className="text-center">
                                                            <strong>
                                                                {record.grouping === "1" ? 'Today\'s Attendance' : 
                                                                 record.grouping === "2" ? 'Recent Attendance' : 
                                                                 record.grouping === "3" ? 'Eligible Students' :
                                                                 'All Remaining Students'}
                                                            </strong>
                                                        </td>
                                                    </tr>
                                                )}
                                                <tr>
                                                    <td>
                                                        {record.student_pic_guid ? (
                                                            <img
                                                                src={buildS3Url(record.student_pic_guid)}
                                                                alt={`${record.first_name} ${record.last_name}`}
                                                                className="img-thumbnail"
                                                                style={{ 
                                                                    width: '80px', 
                                                                    height: '80px', 
                                                                    objectFit: 'contain', 
                                                                    objectPosition: 'center',
                                                                    cursor: 'pointer',
                                                                    transition: 'opacity 0.2s ease'
                                                                }}
                                                                title="Click to view full size image"
                                                                onMouseOver={(e) => e.target.style.opacity = '0.8'}
                                                                onMouseOut={(e) => e.target.style.opacity = '1'}
                                                                onClick={() => handleImageClick(
                                                                    buildS3Url(record.student_pic_guid), 
                                                                    `${record.first_name} ${record.last_name}`
                                                                )}
                                                            />
                                                        ) : (
                                                            <div 
                                                                className="img-thumbnail d-flex align-items-center justify-content-center"
                                                                style={{ 
                                                                    width: '60px', 
                                                                    height: '60px', 
                                                                    backgroundColor: '#f8f9fa',
                                                                    color: '#6c757d'
                                                                }}
                                                            >
                                                                <i className="bi bi-person-fill"></i>
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td>{record.first_name} {record.last_name}</td>
                                                    <td>{record.rank || "No Rank"}</td>
                                                    <td>
                                                        <select
                                                            className="form-control"
                                                            value={record.student_role_id || ""}
                                                            onChange={(e) => handleRoleChange(record.student_id, parseInt(e.target.value))}
                                                        >
                                                            <option value="">Select Role</option>
                                                            {studentRoles.map((role) => (
                                                                <option key={role.student_role_id} value={role.student_role_id}>
                                                                    {role.student_role_desc}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="checkbox"
                                                            checked={record.present}
                                                            onChange={(e) => handleAttendanceChange(record.student_id, e.target.checked)}
                                                        />
                                                    </td>
                                                </tr>
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        <div className="row mt-4">
                            <div className="col-12">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search students..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* Image Modal */}
            <Modal 
                show={showImageModal} 
                onHide={() => setShowImageModal(false)} 
                size="lg"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>{selectedStudentName}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center p-0">
                    <img
                        src={selectedImageUrl}
                        alt={selectedStudentName}
                        style={{
                            width: '100%',
                            height: 'auto',
                            maxHeight: '80vh',
                            objectFit: 'contain'
                        }}
                    />
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default Attendance;
